/** SELECȚIA PE MACHETĂ (Cătălin, 12.10.2026): clic pe un element al fațadei —
 *  un placaj, o balustradă, despărțitorul dintre balcoane, un gol, placa unui
 *  balcon — îl alege; Shift + clic adaugă sau scoate; ⌥/Alt + tragere desenează
 *  o ZONĂ pe fața de sub cursor. Ce s-a ales pleacă la asistent și la agent
 *  (v. `viewer/fatada/selectie.ts`), ca «doar ăsta», «aici», «în zona asta» să
 *  aibă un obiect.
 *
 *  ZONA SE ARATĂ PRIN ELEMENTELE EI (Cătălin, 13.10.2026: «planul 2D în
 *  machetă se vede ciudat»): nu un dreptunghi plutitor, ci exact ce va
 *  schimba agentul — suprafețele continue tăiate la marginea zonei, elementele
 *  bucată întregi după mijloc. Marginile se lipesc de planșee (regulile merg pe
 *  etaje întregi) și de coloane / goluri pe orizontală; o tragere care trece
 *  după colț continuă pe fața vecină.
 *
 *  Macheta LIPEȘTE geometriile pe material (o plasă pe culoare, nu una pe
 *  panou), deci clicul nu nimerește un obiect, ci un triunghi al unei plase
 *  mari. La lipire se ține, pe plasă, unde începe fiecare geometrie-sursă și
 *  id-ul obiectului ei (`TabelAlegere`); triunghiul lovit se caută acolo.
 *
 *  Aici e partea PURĂ, fără scenă: tabelul, căutarea, linia feței, zona. */
import * as THREE from "three";
import type { Fatada3 } from "./sit3d";

/** Unde începe, în triunghiuri, fiecare geometrie lipită într-o plasă, și al
 *  cui obiect e (`""` = nimănui: corpul, terenul, ce nu se alege). */
export interface TabelAlegere {
  starts: number[];
  eids: string[];
  total: number;
}

export function triunghiuri(g: THREE.BufferGeometry): number {
  const n = g.index ? g.index.count : (g.getAttribute("position")?.count ?? 0);
  return Math.floor(n / 3);
}

/** Tabelul unei liste de geometrii, ÎNAINTE de lipire (ordinea e cea a lipirii). */
export function tabelAlegere(list: readonly THREE.BufferGeometry[]): TabelAlegere {
  const starts: number[] = [];
  const eids: string[] = [];
  let t = 0;
  for (const g of list) {
    starts.push(t);
    eids.push(String(g.userData?.eid ?? ""));
    t += triunghiuri(g);
  }
  return { starts, eids, total: t };
}

/** Indicele geometriei-sursă a triunghiului `f` (căutare binară). */
export function indiceLaTriunghi(t: TabelAlegere, f: number): number {
  if (!t.starts.length || f < 0 || f >= t.total) return -1;
  let lo = 0, hi = t.starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (t.starts[mid] <= f) lo = mid; else hi = mid - 1;
  }
  return lo;
}

export function eidLaTriunghi(t: TabelAlegere, f: number): string | null {
  const i = indiceLaTriunghi(t, f);
  return i < 0 ? null : (t.eids[i] || null);
}

/** PĂRȚILE UNUI OBIECT: o placă a fațadei ventilate (`<panou>#p<k>`) și mâna
 *  curentă a unei balustrade (`<balustradă>#mana`) se aleg singure; id-ul
 *  obiectului din document e partea dinaintea lui `#`. */
export const bazaEid = (eid: string): string => {
  const i = eid.indexOf("#");
  return i < 0 ? eid : eid.slice(0, i);
};

/** `eid` e obiectul `alesul` sau o parte a lui. */
export const eAlEi = (eid: string, alesul: string): boolean =>
  eid === alesul || (eid.length > alesul.length && eid.startsWith(alesul) && eid[alesul.length] === "#");

/** Bucățile (`[start, câte]`, în triunghiuri) ale unui obiect într-o plasă —
 *  cu părțile lui (plăcile unui panou, mâna curentă a unei balustrade). */
export function bucatileObiectului(t: TabelAlegere, eid: string): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < t.eids.length; i++) {
    if (!eAlEi(t.eids[i], eid)) continue;
    const s = t.starts[i];
    const e = i + 1 < t.starts.length ? t.starts[i + 1] : t.total;
    if (e > s) out.push([s, e - s]);
  }
  return out;
}

/** O copie a triunghiurilor `[start, start + n)` ale geometriei (lipite), fără
 *  index — pentru evidențiere. Aceleași poziții, deci aceeași adâncime: se
 *  desenează peste cu `LessEqualDepth`, fără să pâlpâie. */
export function triunghiurileDin(g: THREE.BufferGeometry, bucati: [number, number][]): THREE.BufferGeometry | null {
  const pos = g.getAttribute("position") as THREE.BufferAttribute | undefined;
  if (!pos) return null;
  const idx = g.index;
  const n = bucati.reduce((s, [, c]) => s + c, 0);
  if (!n) return null;
  const out = new Float32Array(n * 9);
  let k = 0;
  for (const [s, c] of bucati) {
    for (let t = s; t < s + c; t++) {
      for (let v = 0; v < 3; v++) {
        const i = idx ? idx.getX(t * 3 + v) : t * 3 + v;
        out[k++] = pos.getX(i); out[k++] = pos.getY(i); out[k++] = pos.getZ(i);
      }
    }
  }
  const r = new THREE.BufferGeometry();
  r.setAttribute("position", new THREE.BufferAttribute(out, 3));
  return r;
}

/** Linia unei FEȚE în cadrul machetei: originea (u = 0), direcția, normala
 *  spre afară și lungimea — din panourile ei, care își știu `u0` pe față. */
export interface LiniaFetei {
  face: string;
  corp: number;
  o: [number, number];
  d: [number, number];
  n: [number, number];
  /** Până unde ajung panourile din planul ei, în metri pe față. */
  len: number;
}

/** Suprafețele CONTINUE ale unei fețe (v. `aleseInZona`); tot ele dau linia feței. */
const SUPRAFETE_CONTINUE = new Set(["facade", "plinth", "parapet", "recess"]);

export function liniileFetelor(fatade: readonly Fatada3[]): Map<string, LiniaFetei> {
  const out = new Map<string, LiniaFetei>();
  for (const fat of fatade) {
    for (const p of fat.panouri) {
      if (!p.face || p.poly || p.u0 === undefined || !p.n) continue;
      // numai fețele adevărate ale clădirii: obrajii balcoanelor (sute de fețe
      // de 2 m) nu sunt fețe pe care să tragi o zonă — balconul e al feței lui
      if (!SUPRAFETE_CONTINUE.has(p.surface || "facade")) continue;
      const L = Math.hypot(p.b[0] - p.a[0], p.b[1] - p.a[1]);
      if (L < 0.2) continue;
      const d: [number, number] = [(p.b[0] - p.a[0]) / L, (p.b[1] - p.a[1]) / L];
      // numai panourile din planul feței (nu șpaleți, glafuri, obraji)
      if (Math.abs(d[0] * p.n[0] + d[1] * p.n[1]) > 0.05) continue;
      const f = out.get(p.face);
      if (f) { f.len = Math.max(f.len, p.u0 + L); continue; }
      out.set(p.face, { face: p.face, corp: fat.corp,
                        o: [p.a[0] - d[0] * p.u0, p.a[1] - d[1] * p.u0], d, n: [p.n[0], p.n[1]],
                        len: p.u0 + L });
    }
  }
  return out;
}

/** Punctul `(x, y)` în metri pe față (u) și la ce distanță de linia ei. */
export function peFata(f: LiniaFetei, x: number, y: number): { u: number; dist: number } {
  const dx = x - f.o[0], dy = y - f.o[1];
  return { u: dx * f.d[0] + dy * f.d[1], dist: dx * f.n[0] + dy * f.n[1] };
}

/** Capătul `e` al feței (0 = început, 1 = sfârșit), în plan. */
export function capatul(f: LiniaFetei, e: 0 | 1): [number, number] {
  return e === 0 ? [f.o[0], f.o[1]] : [f.o[0] + f.d[0] * f.len, f.o[1] + f.d[1] * f.len];
}

/** COLȚUL dintre două fețe ale aceluiași corp: capetele care se ating (sub
 *  `tol` metri; finisajul și termosistemul le despart puțin) pe fețe care nu
 *  sunt în prelungire. `null` = nu sunt vecine. */
export function coltulComun(f1: LiniaFetei, f2: LiniaFetei, tol = 1.2): { e1: 0 | 1; e2: 0 | 1 } | null {
  if (f1.face === f2.face || f1.corp !== f2.corp) return null;
  if (Math.abs(f1.d[0] * f2.d[0] + f1.d[1] * f2.d[1]) > 0.9) return null;
  let best: { e1: 0 | 1; e2: 0 | 1 } | null = null, bd = tol;
  for (const e1 of [0, 1] as const) {
    for (const e2 of [0, 1] as const) {
      const a = capatul(f1, e1), b = capatul(f2, e2);
      const dd = Math.hypot(a[0] - b[0], a[1] - b[1]);
      if (dd < bd) { bd = dd; best = { e1, e2 }; }
    }
  }
  return best;
}

/** O bucată de zonă pe o față: intervalul în metri pe ea. `colt` = capetele
 *  lipite de bucata de pe fața vecină (acolo zona trece după colț și nu se
 *  desenează margine). */
export interface BucataZona {
  face: string;
  u0: number;
  u1: number;
  colt?: ("u0" | "u1")[];
}

/** O ZONĂ trasă pe machetă: una sau mai multe bucăți pe fețe vecine ale
 *  aceluiași corp (o tragere care trece după colț), aceleași etaje pe toate.
 *  Cotele `z0`/`z1` sunt lipite de planșee: regulile se aplică pe etaje întregi. */
export interface ZonaMacheta {
  corp: number;
  bucati: BucataZona[];
  z0: number;
  z1: number;
  /** Planșeele (id) și numerele lor, de jos în sus. */
  storeys: string[];
  niveluri: number[];
}

/** Bucățile unei tragerI pe un LANȚ de fețe vecine (prima = unde a început,
 *  ultima = unde e cursorul): pe prima de la `uA` până la colț, cele din mijloc
 *  întregi, pe ultima de la colț până la `uB`. */
export function bucatileLantului(lant: readonly LiniaFetei[], uA: number, uB: number): BucataZona[] {
  if (!lant.length) return [];
  if (lant.length === 1) {
    const f = lant[0];
    return [{ face: f.face, u0: round2(clamp(Math.min(uA, uB), 0, f.len)),
              u1: round2(clamp(Math.max(uA, uB), 0, f.len)) }];
  }
  const out: BucataZona[] = [];
  const uLa = (f: LiniaFetei, e: 0 | 1) => (e === 0 ? 0 : f.len);
  for (let i = 0; i < lant.length; i++) {
    const f = lant[i];
    const spreUrm = i < lant.length - 1 ? coltulComun(f, lant[i + 1])?.e1 : undefined;
    const spreAnt = i > 0 ? coltulComun(lant[i - 1], f)?.e2 : undefined;
    const a = i === 0 ? uA : uLa(f, spreAnt ?? 0);
    const b = i === lant.length - 1 ? uB : uLa(f, spreUrm ?? 1);
    const u0 = round2(clamp(Math.min(a, b), 0, f.len)), u1 = round2(clamp(Math.max(a, b), 0, f.len));
    if (u1 - u0 < 0.05) continue;
    const colt: ("u0" | "u1")[] = [];
    for (const e of [spreAnt, spreUrm]) {
      if (e === undefined) continue;
      colt.push(e === 0 ? "u0" : "u1");
    }
    out.push({ face: f.face, u0, u1, ...(colt.length ? { colt } : {}) });
  }
  return out;
}

/** Etajele corpului pe care le acoperă intervalul `[z0, z1]`: cel puțin o
 *  treime din înălțimea fiecăruia (o margine trasă puțin peste planșeu nu
 *  aduce etajul de deasupra). O tragere întinsă într-un singur etaj (mai
 *  joasă decât o treime din el) ia etajul în care stă. */
export function etajeleZonei(fatade: readonly Fatada3[], corp: number, z0: number, z1: number) {
  const ale = fatade
    .filter((f) => f.corp === corp && !f.acoperis && f.inaltime > 0 && f.storey)
    .sort((a, b) => a.cota - b.cota);
  const out: { storey: string; nivel: number; jos: number; sus: number }[] = [];
  const vazute = new Set<string>();
  const ia = (f: Fatada3) => {
    if (vazute.has(f.storey!)) return;
    vazute.add(f.storey!);
    out.push({ storey: f.storey!, nivel: f.nivel ?? 0, jos: f.cota, sus: f.cota + f.inaltime });
  };
  for (const f of ale) {
    const sus = Math.min(z1, f.cota + f.inaltime), jos = Math.max(z0, f.cota);
    if (sus - jos >= f.inaltime / 3) ia(f);
  }
  if (!out.length) {
    const m = (z0 + z1) / 2;
    const f = ale.find((x) => x.cota <= m && m < x.cota + x.inaltime);
    if (f) ia(f);
  }
  return out;
}

/** Zona unei trageri: bucățile pe lanțul de fețe, etajele acoperite și cotele
 *  LIPITE de planșeele lor. `null` = prea îngustă (sub 0,30 m pe față). */
export function zonaDinTragere(fatade: readonly Fatada3[], lant: readonly LiniaFetei[],
                               a: { u: number; z: number }, b: { u: number; z: number }): ZonaMacheta | null {
  if (!lant.length) return null;
  const bucati = bucatileLantului(lant, a.u, b.u);
  if (bucati.reduce((s, x) => s + (x.u1 - x.u0), 0) < 0.3) return null;
  const et = etajeleZonei(fatade, lant[0].corp, Math.min(a.z, b.z), Math.max(a.z, b.z));
  if (!et.length) return null;
  return { corp: lant[0].corp, bucati,
           z0: round2(Math.min(...et.map((e) => e.jos))), z1: round2(Math.max(...et.map((e) => e.sus))),
           storeys: et.map((e) => e.storey), niveluri: et.map((e) => e.nivel) };
}

/** LIPIREA PE ORIZONTALĂ: liniile care contează pe o față — capetele ei,
 *  limitele coloanelor de apartamente și marginile golurilor de pe ea. */
export function liniiDeLipire(f: LiniaFetei, fatade: readonly Fatada3[], coloane?: readonly number[][]): number[] {
  const out = [0, f.len];
  for (const c of coloane ?? []) out.push(c[0], c[1]);
  for (const fat of fatade) {
    if (fat.corp !== f.corp) continue;
    for (const g of fat.goluri) {
      if (g.n[0] * f.n[0] + g.n[1] * f.n[1] < 0.9) continue;
      const mx = (g.a[0] + g.b[0]) / 2, my = (g.a[1] + g.b[1]) / 2;
      if (Math.abs(peFata(f, mx, my).dist) > 1) continue;
      out.push(peFata(f, g.a[0], g.a[1]).u, peFata(f, g.b[0], g.b[1]).u);
    }
  }
  return [...new Set(out.filter((u) => u >= -0.01 && u <= f.len + 0.01).map(round2))].sort((x, y) => x - y);
}

/** `u` lipit de cea mai apropiată linie sub `tol` metri; altfel neatins. */
export function lipeste(u: number, linii: readonly number[], tol: number): number {
  let best = u, bd = tol;
  for (const l of linii) {
    const dd = Math.abs(l - u);
    if (dd <= bd) { bd = dd; best = l; }
  }
  return best;
}

/** Marginea unei bucăți, în lume, scoasă `iesit` metri în fața feței: cele
 *  două orizontale și verticalele care NU sunt la un colț trecut. Perechi de
 *  puncte, pentru `LineSegments`. */
export function margineaBucatii(f: LiniaFetei, b: BucataZona, z0: number, z1: number,
                                iesit = 0.06): THREE.Vector3[] {
  const p = (u: number, h: number) => new THREE.Vector3(
    f.o[0] + f.d[0] * u + f.n[0] * iesit, f.o[1] + f.d[1] * u + f.n[1] * iesit, h);
  const out = [p(b.u0, z0), p(b.u1, z0), p(b.u0, z1), p(b.u1, z1)];
  if (!b.colt?.includes("u0")) out.push(p(b.u0, z0), p(b.u0, z1));
  if (!b.colt?.includes("u1")) out.push(p(b.u1, z0), p(b.u1, z1));
  return out;
}

/** CE INTRĂ ÎNTR-O ZONĂ, element cu element — aceeași socoteală ca motorul:
 *  - SUPRAFEȚELE CONTINUE (placajul, tencuiala, brâul, soclul, aticul) intră
 *    dacă trec prin zonă, și se colorează TĂIATE la marginile ei;
 *  - ELEMENTELE BUCATĂ (un gol, o balustradă, un despărțitor, un balcon, un
 *    glaf, un șpalet) intră întregi dacă mijlocul lor e în zonă.
 *  Etajul e al elementului (nu cota mijlocului: placa balconului stă sub
 *  pardoseala etajului ei). */
export interface InfoElement {
  storey?: string;
  corp: number;
  face?: string;
  continuu: boolean;
}

const FELURI_BUCATA = new Set(["sill", "flooring"]);

export function infoElemente(fatade: readonly Fatada3[]): Map<string, InfoElement> {
  const out = new Map<string, InfoElement>();
  for (const fat of fatade) {
    const st = fat.storey;
    for (const p of fat.panouri) {
      if (!p.eid) continue;
      out.set(p.eid, { storey: st, corp: fat.corp, face: p.face,
                       continuu: !!p.face && SUPRAFETE_CONTINUE.has(p.surface || "facade")
                                 && !FELURI_BUCATA.has(p.kind) && !p.poly });
    }
    for (const r of fat.parapete) {
      if (r.eid) out.set(r.eid, { storey: st, corp: fat.corp, face: r.face, continuu: false });
    }
    for (const g of fat.goluri) if (g.eid) out.set(g.eid, { storey: st, corp: fat.corp, continuu: false });
    for (const e of fat.balcoaneEid ?? []) if (e) out.set(e, { storey: st, corp: fat.corp, continuu: false });
  }
  return out;
}

/** O bucată de geometrie aleasă: triunghiurile `[start, start + n)` ale unei
 *  plase (sau plasa întreagă, `n` = -1), cu cutia ei în lume. */
export interface Intrare {
  mesh: THREE.Mesh;
  start: number;
  n: number;
  eid: string;
  min: [number, number, number];
  max: [number, number, number];
}

/** Toate bucățile alese ale machetei, cu cutiile lor (o singură trecere pe
 *  triunghiuri; se face o dată pe scenă, la prima zonă). */
export function intrarile(plase: readonly THREE.Mesh[]): Intrare[] {
  const out: Intrare[] = [];
  const v = new THREE.Vector3();
  for (const m of plase) {
    m.updateWorldMatrix(true, false);
    const g = m.geometry;
    const pos = g.getAttribute("position") as THREE.BufferAttribute | undefined;
    if (!pos) continue;
    const idx = g.index;
    const cutie = (s: number, n: number) => {
      const mn: [number, number, number] = [Infinity, Infinity, Infinity];
      const mx: [number, number, number] = [-Infinity, -Infinity, -Infinity];
      for (let t = s; t < s + n; t++) {
        for (let k = 0; k < 3; k++) {
          const i = idx ? idx.getX(t * 3 + k) : t * 3 + k;
          v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(m.matrixWorld);
          if (v.x < mn[0]) mn[0] = v.x; if (v.y < mn[1]) mn[1] = v.y; if (v.z < mn[2]) mn[2] = v.z;
          if (v.x > mx[0]) mx[0] = v.x; if (v.y > mx[1]) mx[1] = v.y; if (v.z > mx[2]) mx[2] = v.z;
        }
      }
      return { mn, mx };
    };
    if (m.userData.eid) {
      const { mn, mx } = cutie(0, triunghiuri(g));
      out.push({ mesh: m, start: 0, n: -1, eid: String(m.userData.eid), min: mn, max: mx });
      continue;
    }
    const t = m.userData.alege as TabelAlegere | undefined;
    if (!t) continue;
    for (let i = 0; i < t.eids.length; i++) {
      if (!t.eids[i]) continue;
      const s = t.starts[i];
      const e = i + 1 < t.starts.length ? t.starts[i + 1] : t.total;
      if (e <= s) continue;
      const { mn, mx } = cutie(s, e - s);
      out.push({ mesh: m, start: s, n: e - s, eid: t.eids[i], min: mn, max: mx });
    }
  }
  return out;
}

/** Fața căreia îi aparține un element bucată: cea scrisă pe el, altfel cea mai
 *  apropiată a corpului lui, cu mijlocul în lungul ei. */
export function fataElementului(x: Intrare, info: InfoElement | undefined, fete: ReadonlyMap<string, LiniaFetei>) {
  if (info?.face && fete.has(info.face)) return info.face;
  const cx = (x.min[0] + x.max[0]) / 2, cy = (x.min[1] + x.max[1]) / 2;
  let best: string | undefined, bd = Infinity;
  for (const f of fete.values()) {
    if (info && f.corp !== info.corp) continue;
    const { u, dist } = peFata(f, cx, cy);
    if (u < -0.3 || u > f.len + 0.3 || dist < -0.6 || dist > 3.5) continue;
    if (Math.abs(dist) < bd) { bd = Math.abs(dist); best = f.face; }
  }
  return best;
}

/** Ce se colorează pentru o zonă: elementele întregi și, pe fiecare bucată a
 *  zonei, suprafețele continue de tăiat la marginile ei. */
export function aleseInZona(intrari: readonly Intrare[], info: ReadonlyMap<string, InfoElement>,
                            fete: ReadonlyMap<string, LiniaFetei>, z: ZonaMacheta) {
  const intregi: Intrare[] = [];
  const taiate = new Map<string, Intrare[]>();
  const storeys = new Set(z.storeys);
  const pe = new Map(z.bucati.map((b) => [b.face, b]));
  for (const x of intrari) {
    const inf = info.get(bazaEid(x.eid));
    if (inf && inf.corp !== z.corp) continue;
    if (inf?.storey ? !storeys.has(inf.storey)
        : (x.min[2] + x.max[2]) / 2 < z.z0 || (x.min[2] + x.max[2]) / 2 > z.z1) continue;
    if (inf?.continuu) {
      const b = pe.get(inf.face!);
      const f = fete.get(inf.face!);
      if (!b || !f) continue;
      const us = [peFata(f, x.min[0], x.min[1]).u, peFata(f, x.max[0], x.max[1]).u,
                  peFata(f, x.min[0], x.max[1]).u, peFata(f, x.max[0], x.min[1]).u];
      if (Math.max(...us) <= b.u0 + 0.01 || Math.min(...us) >= b.u1 - 0.01) continue;
      const l = taiate.get(b.face) ?? [];
      l.push(x);
      taiate.set(b.face, l);
      continue;
    }
    const fata = fataElementului(x, inf, fete);
    const b = fata ? pe.get(fata) : undefined;
    if (!b) continue;
    const u = peFata(fete.get(fata!)!, (x.min[0] + x.max[0]) / 2, (x.min[1] + x.max[1]) / 2).u;
    if (u < b.u0 || u > b.u1) continue;
    intregi.push(x);
  }
  return { intregi, taiate };
}

/** Cele două planuri de tăiere ale unei bucăți: păstrează `u0 ≤ u ≤ u1` pe
 *  fața ei (three păstrează partea cu distanța pozitivă). */
export function planurileBucatii(f: LiniaFetei, b: BucataZona): THREE.Plane[] {
  const od = f.o[0] * f.d[0] + f.o[1] * f.d[1];
  return [new THREE.Plane(new THREE.Vector3(f.d[0], f.d[1], 0), -od - b.u0),
          new THREE.Plane(new THREE.Vector3(-f.d[0], -f.d[1], 0), od + b.u1)];
}

function clamp(v: number, a: number, b: number): number {
  return Math.min(b, Math.max(a, v));
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
